import React, { useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';

import {
  List,
  ListItemText,
  Collapse,
  IconButton,
  TextField,
  ListItem,
  CircularProgress,
  Stack,
  Alert,
} from '@mui/material';

import {
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  HttpsOutlined,
} from '@mui/icons-material';

import { flowActions, State } from '@store/index';
import { Card } from '@common/Card';
import { Group } from './Group';
import { Resource, ResourcePermission } from '@store/types';
import { LoadingButton } from '@common/Button';
import { Loader } from '@common/hooks/useLoader';
import { createWorkspaceRole, editWorkspaceRole, listWorkspaceRoles, PermissionGroup } from '@requests/roles';
import { usePermissions } from '@common/hooks/usePermissions';
import { SubText } from '@common/SubText/SubText';

const Container = styled.div<{ oneGroup?: boolean }>`
  padding: 12px;
  overflow-y: auto;

  svg {
    font-size: 20px;
  }

  & .MuiListItem-root {
    background-color: ${({ theme }) => theme.background.light};
  }

  .group:first-child {
    #header {
      border-radius: 6px 6px 0 0;
    }
  }

  .group:last-child {
    #header {
      border-radius: 0 0 6px 6px;
    }
  }

  .group {
    #header {
      border-radius: ${({ oneGroup }) => (oneGroup ? '6px !important' : '6px 6px 0 0')};
    }
  }
`;

const ListHeader = styled.div<{ open: boolean }>`
  background-color: ${({ theme }) => theme.background.lighter};
  font-size: 12px;
  color: ${({ theme }) => theme.text.subtext};
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 4px;
  padding-top: 12px;
  /* opacity: ${({ open }) => (open ? 0.38 : 1)}; */
`;

export const PermissionRoles: React.FC = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { admin } = usePermissions(service_id as string, Resource.ADMIN);

  const orgId = useSelector((state: State) => state.auth.org_id);

  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [deselectedResources, setDeselectedResources] = useState<string[]>([]);
  const [groupName, setGroupName] = useState<string | null>(null);

  const {
    data: groups = [],
    isLoading: isRolesLoading,
    refetch,
  } = useQuery({
    queryKey: ['groups', { serviceId: service_id, orgId }],
    queryFn: listWorkspaceRoles,
    staleTime: 30000,
    enabled: Boolean(service_id && orgId),
  });

  const { mutateAsync: create, isPending: isCreateLoading } = useMutation({
    mutationFn: createWorkspaceRole,
    onMutate: () => {
      enqueueSnackbar('Creating permission group', { variant: 'info', autoHideDuration: 5000 });
    },
    onSuccess: async (data: { role_id?: string; error?: string }) => {
      if (!data) return;

      if (data?.error) {
        enqueueSnackbar(data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to create a permission group!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      await refetch();
      reset();
      if (data?.role_id) setEditing(data.role_id);

      enqueueSnackbar('Permission group created successfully', { variant: 'success', autoHideDuration: 5000 });
    },
  });

  const { mutateAsync: edit, isPending: isEditLoading } = useMutation({
    mutationFn: editWorkspaceRole,
    onMutate: () => {
      enqueueSnackbar('Updating permission group', { variant: 'info', autoHideDuration: 5000 });
    },
    onSuccess: async () => {
      await refetch();
      reset();
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      enqueueSnackbar('Permission group updated successfully', { variant: 'success', autoHideDuration: 5000 });
    },
  });

  const toggleEdit = (group: PermissionGroup) => {
    if (editing && editing !== group.id) return;

    if (editing === group.id) {
      const resources = group.resources.map((resource) => ({
        ...resource,
        enabled:
          (resource.enabled && !deselectedResources.includes(resource.id)) ||
          (!resource.enabled && selectedResources.includes(resource.id)),
      }));

      const modifiedGroup = { ...group, role_name: groupName || group.role_name, resources };
      edit({ serviceId: service_id as string, group: modifiedGroup });
    } else {
      setEditing(group.id);
      setOpen(group.id);
    }
  };

  const toggleOpen = (group: PermissionGroup) => {
    if (editing) return;

    if (open === group.id) setOpen(null);
    else setOpen(group.id);
  };

  const reset = () => {
    setSelectedResources([]);
    setDeselectedResources([]);
    setEditing(null);
    setGroupName(null);
  };

  const cancelOrDelete = (group: PermissionGroup) => {
    if (editing) reset();
    else dispatch(flowActions.setDeletePermissionGroup(group.id));
  };

  const changeGroupName = (value: string) => {
    if (!value) setGroupName('');
    else setGroupName(value);
  };

  const selectRow = (resource: ResourcePermission) => {
    if (!editing) return;

    if (resource.enabled) {
      if (!deselectedResources.includes(resource.id))
        setDeselectedResources((prevState) => prevState.concat(resource.id));
      else setDeselectedResources((prevState) => prevState.filter((id) => id !== resource.id));

      return;
    }

    if (!selectedResources.includes(resource.id)) setSelectedResources((prevState) => prevState.concat(resource.id));
    else setSelectedResources((prevState) => prevState.filter((id) => id !== resource.id));
  };

  const addGroup = () => {
    const serviceId = service_id as string;
    create({ serviceId, roleName: 'New Group', resourcePermissions: [] });
  };

  const body = (
    <List component='nav' sx={{ width: '100%' }}>
      {!isRolesLoading && groups.length === 0 && (
        <Alert severity={admin ? 'info' : 'warning'}>
          {!admin
            ? `This workspace does not have any permission groups. Ask a workspace admin to create some.`
            : `This workspace does not have any permission groups. Click the button below to create one!`}
        </Alert>
      )}
      {isRolesLoading && <Loader />}
      {groups.map((group) => {
        const { id, role_name, resources } = group;

        const isEditing = editing === id;
        const isOpen = open === id || isEditing;

        const workflowResources = resources.filter(
          (resource) => resource.resource_type === 'flow' && (resource.enabled || isEditing)
        );

        const bucketResources = resources.filter(
          (resource) => resource.resource_type === 'bucket' && (resource.enabled || isEditing)
        );

        const groupNameError =
          ((groupName && groupName?.length > 26) ||
            groups.some((group) => group.role_name === groupName && role_name !== groupName)) &&
          isEditing;

        return (
          <div key={id} className='group'>
            <ListItem id='header'>
              {isEditing ? (
                <TextField
                  fullWidth
                  variant='standard'
                  error={groupNameError}
                  value={groupName || groupName === '' ? groupName : group.role_name}
                  onChange={(e) => changeGroupName(e.target.value)}
                />
              ) : (
                <ListItemText primary={role_name} />
              )}
              {isEditLoading && isEditing && (
                <IconButton size='small' disabled>
                  <CircularProgress size={16} />
                </IconButton>
              )}
              {!(isEditLoading && isEditing) && admin && (
                <IconButton size='small' disabled={groupNameError} onClick={() => toggleEdit(group)}>
                  {isEditing ? <SaveIcon /> : <EditIcon />}
                </IconButton>
              )}
              {admin && (
                <IconButton onClick={() => cancelOrDelete(group)} size='small'>
                  {isEditing ? <CancelIcon /> : <DeleteIcon />}
                </IconButton>
              )}
              <IconButton size='small' disabled={isEditing} onClick={() => toggleOpen(group)}>
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </ListItem>
            <Collapse in={isOpen} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                {resources.filter((resource) => resource.enabled).length === 0 && !isEditing && (
                  <ListItem sx={{ pl: 3 }}>
                    <SubText>This permission group does not have any resources assigned.</SubText>
                  </ListItem>
                )}
                {workflowResources.length > 0 && <ListHeader open={isOpen && !isEditing}>Workflows</ListHeader>}
                {workflowResources.length > 0 &&
                  workflowResources.map((resource) => (
                    <Group
                      key={`${id}-${resource.id}`}
                      resource={resource}
                      editing={isEditing}
                      selected={
                        Boolean(resource?.enabled || selectedResources.includes(resource.id)) &&
                        !deselectedResources.includes(resource.id)
                      }
                      onSelect={selectRow}
                    />
                  ))}

                {bucketResources.length > 0 && <ListHeader open={isOpen && !isEditing}>Buckets</ListHeader>}
                {bucketResources.length > 0 &&
                  bucketResources.map((resource) => (
                    <Group
                      key={`${id}-${resource.id}`}
                      resource={resource}
                      editing={isEditing}
                      selected={
                        Boolean(resource?.enabled || selectedResources.includes(resource.id)) &&
                        !deselectedResources.includes(resource.id)
                      }
                      onSelect={selectRow}
                    />
                  ))}
              </List>
            </Collapse>
          </div>
        );
      })}
    </List>
  );

  return (
    <Container oneGroup={groups.length === 1}>
      <Card
        title='Permission Groups'
        icon={<HttpsOutlined />}
        description='A permission group can be assigned to a user to give them a collection of permissions.'
        body={
          <Stack spacing={2}>
            {body}
            {admin && (
              <LoadingButton fullWidth minHeight='32px' loading={isCreateLoading} onClick={addGroup}>
                Add Group
              </LoadingButton>
            )}
          </Stack>
        }
      />
    </Container>
  );
};
